import { Components, createTheme } from '@mui/material'
import { border } from './border'
import { color, themeColor, xxllncColor } from './color'

const material = createTheme()

export const muiOverrides: Components = {
  MuiTypography: {
    styleOverrides: {
      root: {
        ['&.MuiTypography-subtitle1']: {
          fontWeight: 600,
        },
      },
    },
  },
  MuiTextField: {
    defaultProps: {
      fullWidth: true,
    },
  },
  MuiPaper: {
    styleOverrides: {
      root: {
        border: '1px solid ' + themeColor.slate10,
        backgroundClip: 'padding-box',
        borderRadius: border.card.radius,
      },
      elevation1: {
        boxShadow: '0 1px 3px rgba(15,23,42,0.04), 0 1px 2px rgba(15,23,42,0.02)',
      },
    },
  },
  MuiDialogActions: {
    styleOverrides: {
      root: {
        ['& svg']: {
          marginRight: 10,
        },
      },
    },
  },
  MuiTabs: {
    styleOverrides: {
      root: {
        backgroundColor: 'none',
        [material.breakpoints.down('lg')]: {
          padding: material.spacing(1.5, 3, 0, 3),
        },
        [material.breakpoints.up('md')]: {
          padding: material.spacing(1.5, 6, 0, 6),
        },
        ['& .Mui-selected']: {
          backgroundColor: themeColor.blue10,
          color: themeColor.blue,
          borderRadius: border.dropdown.radius,
        },
      },
      indicator: {
        display: 'none',
      },
    },
  },
  MuiButton: {
    styleOverrides: {
      root: {
        color: themeColor.slate60,
        borderRadius: border.button.radius,
        minWidth: 'auto',
        fontWeight: 500,
        textTransform: 'none' as const,
      },
      contained: {
        backgroundColor: themeColor.blue,
        color: '#fff',
        boxShadow: 'none',
        ['&:hover']: {
          boxShadow: 'none',
        },
      },
      outlinedPrimary: {
        border: border.thin + ' solid ' + themeColor.slate20,
        ['&:hover']: {
          border: border.thin + ' solid ' + themeColor.slate40,
          backgroundColor: themeColor.slate05,
        },
      },
      startIcon: {
        marginRight: -2,
        marginLeft: 8,
      },
    },
  },
  MuiButtonBase: {
    styleOverrides: {
      root: {
        ['&:hover:active::after']: {
          content: '""',
          display: 'block',
          width: '100%',
          height: '100%',
          position: 'absolute',
          top: 0,
          right: 0,
          backgroundColor: 'currentColor',
          opacity: 0.3,
          borderRadius: 'inherit',
        },
        ['&.MuiButton-contained']: {
          ['&:hover']: {
            backgroundColor: themeColor.blue80,
          },
        },
      },
    },
    defaultProps: {
      disableRipple: true,
    },
  },
  MuiCard: {
    styleOverrides: {
      root: {
        marginBottom: material.spacing(2),
      },
    },
  },
  MuiAppBar: {
    styleOverrides: {
      root: {
        borderRadius: '0 !important',
      },
      colorSecondary: {
        backgroundColor: '#fff',
        border: 0,
        borderBottom: border.thin + ' solid ' + themeColor.slate10,
      },
    },
  },
  MuiLinearProgress: {
    styleOverrides: {
      colorPrimary: {
        backgroundColor: themeColor.slate10,
      },
      barColorPrimary: {
        backgroundColor: themeColor.blue,
      },
    },
  },
  MuiOutlinedInput: {
    styleOverrides: {
      root: {
        borderRadius: border.input.radius,
      },
    },
  },
  MuiDivider: {
    styleOverrides: {
      root: {
        display: 'none',
      },
    },
  },
  MuiGrid: {
    styleOverrides: {
      root: {
        ['& .ra-field']: {
          display: 'flex',
          padding: material.spacing(.5),
          ['& a']: {
            wordWrap: 'break-word',
          },
        },
      },
      container: {
        ['&.MuiGrid-spacing-xs-1']: {
          marginTop: material.spacing(2),
        },
        ['&.MuiGrid-spacing-xs-2']: {
          marginTop: material.spacing(2),
        },
        ['&.MuiGrid-spacing-xs-3']: {
          marginTop: material.spacing(2),
        }
      },
    },
  },
  MuiPopover: {
    styleOverrides: {
      paper: {
        background: '#fff',
        border: border.thin + ' solid ' + themeColor.slate10,
        borderRadius: border.dropdown.radius,
        boxShadow: '0px 4px 12px rgba(15, 23, 42, 0.08), 0px 2px 4px rgba(15, 23, 42, 0.04)',
        ['& .MuiMenu-list']: {
          boxSizing: 'border-box',
          padding: material.spacing(1),
          ['& .MuiMenuItem-root']: {
            borderRadius: border.radius.sm,
            color: themeColor.slate80,
            marginBottom: 0,
            marginTop: 2,
            width: '100%',
            fontSize: 14,
            ['& .MuiSvgIcon-root']: {
              fill: themeColor.slate60,
            },
            ['&:first-child']: {
              marginTop: 0,
            },
            ['&:hover']: {
              color: themeColor.black,
              background: themeColor.slate05,
              ['& .MuiSvgIcon-root']: {
                fill: themeColor.slate80,
              },
            },
          },
          ['& .Mui-selected']: {
            background: themeColor.blue10,
            color: themeColor.blue,
            ['& .MuiSvgIcon-root']: {
              fill: themeColor.blue,
            },
          },
        },
      },
    },
  },
  MuiStepIcon: {
    styleOverrides: {
      root: {
        'color': themeColor.slate40,
        'fontWeight': 'bold',
        '&.Mui-active': {
          color: themeColor.blue,
        },
      },
    },
  },
  MuiStepLabel: {
    styleOverrides: {
      label: {
        '&.MuiStepLabel-alternativeLabel': {
          fontWeight: 400,
          fontSize: '8pt',
          color: themeColor.slate80,
        },
      },
    },
  },
  MuiListItemIcon: {
    styleOverrides: {
      root: {
        color: themeColor.slate60,
      },
    },
  },
}
